import React, { useState, useEffect } from 'react';
import { ArrowRight, ArrowLeft, Eye, EyeOff } from 'lucide-react';
import API from '../services/API';
import AccountLockedScreen from './AccountLockedScreen';
import ParentLockedScreen from './ParentLockedScreen';

const APPROVED_LEADERS = [
  { label: '1181 - Phạm Thắm',      value: '0388179985' },
  { label: '1182 - THANH HẢI',      value: '0983173646' },
  { label: '1185 - Nguyễn Trinh',   value: '0389636385' },
  { label: '1184 - MỸ LỆ',          value: '0937788662' },
  { label: '1186 - Bích Ly',        value: '0888784721' },
  { label: '1187 - Yến Linh',       value: '0388631227' },
  { label: '2209 - Ánh Xuân',       value: '0918189239' },
  { label: '1188 - Phan Oanh',      value: '0965074562' },
  { label: '1368 - Phạm Giang',     value: '0904077644' },
  { label: '2025 - Hường Nguyễn',   value: '0969532959' },
];

const LoginPage = ({ 
  onLogin, 
  onRegister, 
  setMainPageUrl, 
  setResultPageUrl, 
  setPhoneNumber, 
  setError, 
  error, // Add error prop to display error messages
  loading, 
  setLoading,
  setUserInfo, // Add this prop to receive user info setter
  mainPageUrl, // Add campaign main URL
  resultPageUrl, // Add campaign result URL
  campaignInfo // Add campaign info
}) => {
  const [inputPhone, setInputPhone] = useState('');
  const [inputMainUrl, setInputMainUrl] = useState(mainPageUrl || '');
  const [inputResultUrl, setInputResultUrl] = useState(resultPageUrl || '');
  // Multi-step login state: 'phone' | 'password' | 'set-password'
  const [loginStep, setLoginStep] = useState('phone');
  const [inputPassword, setInputPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  // Lock state: hiển thị màn AccountLocked / ParentLocked khi BE trả errorCode tương ứng.
  // null = login form bình thường. Reset bằng nút "Quay lại đăng nhập" trên màn lock.
  const [lockState, setLockState] = useState(null); // {type: 'account'|'parent', detail}

  // Lấy redirect path từ URL params
  const getRedirectPath = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const redirect = urlParams.get('redirect');
    return redirect || '/chien-dich';
  };

  // Lấy thông tin về nơi sẽ redirect
  const getRedirectInfo = () => {
    const redirectPath = getRedirectPath();
    if (redirectPath === '/chien-dich') {
      return 'danh sách chiến dịch';
    } else if (redirectPath.startsWith('/chien-dich/')) {
      const campaignCode = redirectPath.split('/')[2];
      return `chiến dịch ${campaignCode}`;
    }
    return 'trang chính';
  };

  // Update input fields when campaign URLs change
  useEffect(() => {
    if (mainPageUrl) {
      setInputMainUrl(mainPageUrl);
      console.log('🔄 LoginPage - Updated main URL from campaign:', mainPageUrl);
    }
    if (resultPageUrl) {
      setInputResultUrl(resultPageUrl);
      console.log('🔄 LoginPage - Updated result URL from campaign:', resultPageUrl);
    }
  }, [mainPageUrl, resultPageUrl]);

  // Shared helper: process successful login after API returns valid user data
  const processLoginSuccess = async (userCheck, formattedPhone) => {
    // Set iframe URLs - use campaign URLs if available
    const finalMainUrl = campaignInfo?.mainUrl || inputMainUrl;
    const finalResultUrl = campaignInfo?.successUrl || inputResultUrl;
    if (finalMainUrl) setMainPageUrl(finalMainUrl);
    if (finalResultUrl) setResultPageUrl(finalResultUrl);

    let userInfo = userCheck.userInfo;
    const accessToken = userCheck.accessToken;

    // Fetch user info if not returned from login
    if (!userInfo && setUserInfo) {
      try {
        userInfo = await API.getUserInfo(formattedPhone);
      } catch (error) {
        console.warn('Could not get detailed user info:', error);
        userInfo = { phoneNumber: formattedPhone, name: formattedPhone, id: null };
      }
    }
    if (setUserInfo && userInfo) setUserInfo(userInfo);

    // Validate roleId - check both possible response structures
    const roleId = userInfo?.userInfo?.roleId || userInfo?.userData?.roleId;
    if (userInfo?.userInfo && (!roleId || roleId <= 0)) {
      setError('SĐT không tồn tại');
      return;
    }
    if (roleId === 15) {
      setError('SĐT không được phép');
      return;
    }

    // Campaign eligibility — fire-and-forget. Trước đây 2 API này await tuần tự nhưng chỉ console.log,
    // không dùng kết quả → block login ~1-2s không cần thiết. Component /chien-dich/* tự fetch khi user navigate.
    let campaignCode = campaignInfo?.campaignCode || null;
    if (!campaignCode) {
      const pathname = window.location.pathname;
      const campaignMatch = pathname.match(/^\/([a-zA-Z0-9_-]+)$/);
      if (campaignMatch && campaignMatch[1] !== 'chien-dich') {
        campaignCode = campaignMatch[1];
      }
    }
    if (campaignCode) {
      Promise.allSettled([
        API.checkCampaignEligibility(formattedPhone, campaignCode, campaignInfo?.organizationId),
        API.checkCampaignParticipation(formattedPhone, campaignCode),
      ]).catch(() => {});
    }

    // Prepare login info
    const loginUserInfo = {
      phoneNumber: formattedPhone,
      id: userInfo?.userInfo?.id || userInfo?.id,
      code: userInfo?.userInfo?.code || userInfo?.code || userInfo?.userInfo?.id || userInfo?.id,
      name: userInfo?.userInfo?.name || userInfo?.name || formattedPhone,
      email: userInfo?.userInfo?.email || userInfo?.email,
      avatar: userInfo?.userInfo?.avatar || userInfo?.avatar,
    };

    // GHI CHÚ: Trước đây chặn login khi `phone_parent` thiếu, bắt user sang Chiến dịch 01.
    // Bỏ vì DB trigger `trg_update_team_chain` (xem user.entity.ts) đã auto compute
    // team_leader / f_logic / level từ phone_parent — không còn lý do chặn ở client.
    setPhoneNumber(formattedPhone);
    onLogin(loginUserInfo, accessToken);
  };

  const handleLogin = async () => {
    if (!inputPhone || inputPhone.length < 10) {
      setError('Vui lòng nhập số điện thoại hợp lệ');
      return;
    }

    // Campaign URL validation only on phone step for specific campaigns
    if (loginStep === 'phone') {
      const redirectPath = getRedirectPath();
      const isSpecificCampaign = redirectPath.startsWith('/chien-dich/') && redirectPath !== '/chien-dich';
      if (isSpecificCampaign) {
        if (campaignInfo) {
          if (!campaignInfo.mainUrl && !campaignInfo.successUrl) {
            setError('Campaign không có URL hợp lệ');
            return;
          }
        } else {
          setError('Không có thông tin chiến dịch');
          return;
        }
      }
    }

    setLoading(true);
    setError('');

    try {
      const formattedPhone = API.formatPhoneNumber(inputPhone);
      const campaignOrganizationId = campaignInfo?.organizationId;

      if (loginStep === 'password') {
        // Password step: use CungVeDich login API with password
        const loginResult = await API.login(formattedPhone, inputPassword, campaignOrganizationId);
        if (loginResult.errorCode) {
          if (loginResult.errorCode === 'ACCOUNT_LOCKED') {
            setLockState({ type: 'account', detail: loginResult.detail });
            setLoading(false);
            return;
          }
          if (loginResult.errorCode === 'PARENT_LOCKED') {
            setLockState({ type: 'parent', detail: loginResult.detail });
            setLoading(false);
            return;
          }
          if (loginResult.errorCode === 'INVALID_PASSWORD') {
            setError('Mật khẩu không đúng. Vui lòng thử lại.');
          } else {
            setError('Đăng nhập thất bại. Vui lòng thử lại.');
          }
          setLoading(false);
          return;
        }
        await processLoginSuccess(loginResult, formattedPhone);
      } else {
        // Gate: chỉ CHẶN khi check-agent xác định rõ. agent=null → KHÔNG kiểm tra được (BE chưa
        // có endpoint / lỗi) → BỎ QUA, đăng nhập bình thường (tránh chặn nhầm chủ tịch/lãnh đạo
        // /tài khoản hợp lệ khác). Chỉ chặn đại lý role-14 đang chờ duyệt / bị từ chối.
        const agent = await API.checkAgent(formattedPhone);
        if (agent) {
          if (!agent.exists) {
            setError('Số điện thoại chưa tồn tại trên hệ thống — vui lòng đăng ký');
            setLoading(false);
            return;
          }
          if (agent.isAgent && agent.approvalStatus === 'pending') {
            setError('Hồ sơ của bạn đang chờ công ty duyệt. Vui lòng đợi được gọi xác minh.');
            setLoading(false);
            return;
          }
          if (agent.isAgent && agent.approvalStatus === 'rejected') {
            setError('Hồ sơ đăng ký đã bị từ chối' + (agent.approvalNote ? `: ${agent.approvalNote}` : '') + '. Vui lòng liên hệ cấp trên.');
            setLoading(false);
            return;
          }
        }

        // Phone step: first try CungVeDich login (no password) to check requireLogin status
        const loginResult = await API.login(formattedPhone, undefined, campaignOrganizationId);
        if (loginResult.errorCode) {
          if (loginResult.errorCode === 'ACCOUNT_LOCKED') {
            setLockState({ type: 'account', detail: loginResult.detail });
            setLoading(false);
            return;
          }
          if (loginResult.errorCode === 'PARENT_LOCKED') {
            setLockState({ type: 'parent', detail: loginResult.detail });
            setLoading(false);
            return;
          }
          if (loginResult.errorCode === 'PASSWORD_REQUIRED') {
            setLoginStep('password');
            setLoading(false);
            return;
          }
          if (loginResult.errorCode === 'PASSWORD_NOT_SET') {
            setLoginStep('set-password');
            setLoading(false);
            return;
          }
        }
        // Tối ưu tốc độ login: nếu CungVeDich /auth/login đã trả success + accessToken, dùng luôn —
        // KHÔNG cần gọi thêm /auth/login-with-phone (Yody legacy). Save 1 round-trip ~1-2s.
        if (loginResult?.success && loginResult?.accessToken) {
          await processLoginSuccess(loginResult, formattedPhone);
          return;
        }
        // Fallback Yody legacy chỉ khi CungVeDich không trả token (hiếm — user chưa migrate)
        const userCheck = await API.checkUserExists(formattedPhone, campaignOrganizationId);
        if (userCheck?.errorCode === 'ACCOUNT_LOCKED') {
          setLockState({ type: 'account', detail: userCheck.detail });
          setLoading(false);
          return;
        }
        if (userCheck?.errorCode === 'PARENT_LOCKED') {
          setLockState({ type: 'parent', detail: userCheck.detail });
          setLoading(false);
          return;
        }
        await processLoginSuccess(userCheck, formattedPhone);
      }
    } catch (err) {
      console.log('Login error:', err.message);

      const isUserNotFound = err.message && (
        err.message.includes('401') ||
        err.message.includes('404') ||
        err.message.includes('User not found')
      ) && !err.message.includes('accessToken') &&
        !err.message.includes('storage') &&
        !err.message.includes('lưu thông tin xác thực');

      if (isUserNotFound) {
        setError('Bạn chưa tham gia chiến dịch 1. Liên hệ cấp trên để được hỗ trợ');
      } else {
        setError(err.message || 'Có lỗi xảy ra, vui lòng thử lại');
      }
    } finally {
      setLoading(false);
    }
  };

  // Nút "Đăng ký mới": SĐT đã là đại lý → mời đăng nhập; ngược lại sang form đăng ký (mang theo SĐT).
  const handleRegisterClick = async () => {
    if (!inputPhone || inputPhone.length < 10) {
      setError('Vui lòng nhập số điện thoại hợp lệ');
      return;
    }
    setError('');
    setLoading(true);
    try {
      const formattedPhone = API.formatPhoneNumber(inputPhone);
      const agent = await API.checkAgent(formattedPhone);
      // agent=null (chưa kiểm tra được) → vẫn cho vào form (BE sẽ chặn nếu trùng lúc submit).
      if (agent && agent.exists) {
        // Đã là đại lý (role 14): chờ duyệt → báo chờ; đã duyệt → mời đăng nhập (không đăng ký lại).
        if (agent.isAgent) {
          if (agent.approvalStatus === 'pending') {
            setError('Hồ sơ của bạn đang chờ duyệt. Vui lòng đợi công ty xác minh.');
            return;
          }
          setError('Số điện thoại đã là đại lý — vui lòng đăng nhập');
          return;
        }
        // exists nhưng role ≠ 14 (vd khách lẻ role 15): CHO PHÉP đăng ký để nâng cấp thành đại lý
        // → rơi xuống dưới vào form. BE registerAgent sẽ EDIT record + đặt chờ duyệt lại.
      }
      setPhoneNumber(formattedPhone);
      onRegister && onRegister();
    } catch (e) {
      // Lỗi mạng khi kiểm tra → vẫn cho vào form đăng ký
      setPhoneNumber(API.formatPhoneNumber(inputPhone));
      onRegister && onRegister();
    } finally {
      setLoading(false);
    }
  };

  // Handle setting initial password for requireLogin users without a password
  const handleSetPassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      setError('Mật khẩu phải có ít nhất 6 ký tự');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Mật khẩu xác nhận không khớp');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const formattedPhone = API.formatPhoneNumber(inputPhone);
      const campaignOrganizationId = campaignInfo?.organizationId;

      // Set initial password
      await API.changePasswordPublic(formattedPhone, newPassword, campaignOrganizationId);

      // Auto-login with the new password via CungVeDich login API
      const loginResult = await API.login(formattedPhone, newPassword, campaignOrganizationId);

      if (loginResult.errorCode) {
        setError('Đặt mật khẩu thành công nhưng đăng nhập tự động thất bại. Vui lòng nhập mật khẩu vừa tạo để đăng nhập.');
        setLoginStep('password');
        setInputPassword('');
        return;
      }

      await processLoginSuccess(loginResult, formattedPhone);
    } catch (err) {
      setError(err.message || 'Không thể đặt mật khẩu. Vui lòng thử lại.');
    } finally {
      setLoading(false);
    }
  };

  // Return to phone step, clear all password fields
  const handleBack = () => {
    setLoginStep('phone');
    setInputPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setShowPassword(false);
    setShowNewPassword(false);
    setError('');
  };

  // Reset lockState + về phone step khi user click "Quay lại đăng nhập"
  const handleBackFromLock = () => {
    setLockState(null);
    setLoginStep('phone');
    setInputPassword('');
    setError('');
  };

  // Lock screen — render thay vì form khi BE trả errorCode lock
  if (lockState?.type === 'account') {
    return <AccountLockedScreen detail={lockState.detail} onBack={handleBackFromLock} />;
  }
  if (lockState?.type === 'parent') {
    return <ParentLockedScreen detail={lockState.detail} onBack={handleBackFromLock} />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-brand-50 to-brand-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center">
            <img 
              src="/images/logo-yodywhite.png" 
              alt="Yody Logo" 
              className="w-32 h-32 object-contain"
            />
          </div>
          <h1 className="text-2xl font-bold text-gray-800">
            {loginStep === 'set-password' ? 'Đặt mật khẩu' : 'Đăng nhập'}
          </h1>
          <p className="text-gray-600 mt-2">
            {loginStep === 'phone' && 'Nhập số điện thoại để tiếp tục'}
            {loginStep === 'password' && `Nhập mật khẩu cho SĐT ${inputPhone}`}
            {loginStep === 'set-password' && 'Bạn chưa đặt mật khẩu cho tài khoản này, vui lòng nhập mật khẩu mới để đăng nhập'}
          </p>
          
          {/* Hiển thị thông tin redirect - only on phone step */}
          {loginStep === 'phone' && (
            <div className="mt-4 p-3 bg-brand-50 rounded-lg border border-brand-200">
              <p className="text-sm text-brand-700">
                <span className="font-medium">Sau khi đăng nhập:</span> Bạn sẽ được chuyển đến {getRedirectInfo()}
              </p>
            </div>
          )}
        </div>

        <div className="space-y-4">
          {/* Phone step */}
          {loginStep === 'phone' && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Số điện thoại
                </label>
                <input
                  type="tel"
                  value={inputPhone}
                  onChange={(e) => setInputPhone(e.target.value)}
                  placeholder="0123456789"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                  disabled={loading}
                  onKeyDown={(e) => e.key === 'Enter' && handleLogin()}
                />
              </div>
              {(() => {
                const phoneReady = inputPhone && inputPhone.replace(/\s/g, '').length >= 10;
                return (
                  <div className="space-y-3">
                    <button
                      onClick={handleLogin}
                      disabled={loading || !phoneReady}
                      className="w-full bg-brand-500 text-white py-3 rounded-lg font-medium hover:bg-brand-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      {loading ? (
                        <>
                          <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                          Đang xử lý...
                        </>
                      ) : (
                        <>
                          Đăng nhập
                          <ArrowRight className="w-5 h-5" />
                        </>
                      )}
                    </button>
                    <button
                      onClick={handleRegisterClick}
                      disabled={loading || !phoneReady}
                      className="w-full bg-white text-brand-600 border-2 border-brand-500 py-3 rounded-lg font-medium hover:bg-brand-50 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      Đăng ký mới
                    </button>
                    {!phoneReady && (
                      <p className="text-center text-xs text-gray-400">Nhập số điện thoại để đăng nhập hoặc đăng ký</p>
                    )}
                  </div>
                );
              })()}
            </>
          )}

          {/* Password step */}
          {loginStep === 'password' && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Số điện thoại</label>
                <input
                  type="tel"
                  value={inputPhone}
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg bg-gray-50 text-gray-500"
                  disabled
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Mật khẩu</label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={inputPassword}
                    onChange={(e) => setInputPassword(e.target.value)}
                    placeholder="Nhập mật khẩu"
                    className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                    disabled={loading}
                    onKeyDown={(e) => e.key === 'Enter' && handleLogin()}
                    autoFocus
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>
              <button
                onClick={handleLogin}
                disabled={loading || !inputPassword}
                className="w-full bg-brand-500 text-white py-3 rounded-lg font-medium hover:bg-brand-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Đang xử lý...
                  </>
                ) : 'Đăng nhập'}
              </button>
              <button
                type="button"
                onClick={handleBack}
                className="w-full text-brand-600 hover:text-brand-700 text-sm font-medium flex items-center justify-center gap-1"
              >
                <ArrowLeft className="w-4 h-4" /> Quay lại
              </button>
            </>
          )}

          {/* Set-password step */}
          {loginStep === 'set-password' && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Số điện thoại</label>
                <input
                  type="tel"
                  value={inputPhone}
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg bg-gray-50 text-gray-500"
                  disabled
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Mật khẩu mới</label>
                <div className="relative">
                  <input
                    type={showNewPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Nhập mật khẩu mới (ít nhất 6 ký tự)"
                    className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                    disabled={loading}
                    autoFocus
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showNewPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Xác nhận mật khẩu</label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Nhập lại mật khẩu"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                  disabled={loading}
                  onKeyDown={(e) => e.key === 'Enter' && handleSetPassword()}
                />
              </div>
              <button
                onClick={handleSetPassword}
                disabled={loading || !newPassword || !confirmPassword}
                className="w-full bg-brand-500 text-white py-3 rounded-lg font-medium hover:bg-brand-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Đang xử lý...
                  </>
                ) : 'Đặt mật khẩu'}
              </button>
              <button
                type="button"
                onClick={handleBack}
                className="w-full text-brand-600 hover:text-brand-700 text-sm font-medium flex items-center justify-center gap-1"
              >
                <ArrowLeft className="w-4 h-4" /> Quay lại
              </button>
            </>
          )}
        </div>
        
        {/* Error message display with clickable link */}
        <div className="mt-4">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="text-sm text-red-800">
                {error.includes('https://app.yodyphuonganh.vn/') ? (
                  <>
                    <span>Bạn chưa tham gia chiến dịch 1. Liên hệ cấp trên để được hỗ trợ</span>
                  </>
                ) : (
                  error
                )}
              </div>
            </div>
          )}
        </div>
        
      </div>
    </div>
  );
};

export default LoginPage;
